import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ChangeUserAccessParams, useChangeUserAccessMutation } from 'entities/User';

interface HookArgs {
  userId: string | undefined;
  updatedData: Nullable<ChangeUserAccessParams>;
  onSuccess?: () => void;
}

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  handleChangeUserAccess: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useChangeUserAccess = ({ userId, updatedData, onSuccess }: HookArgs): HookApi => {
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation('common');

  const [changeUserAccess, { isLoading }] = useChangeUserAccessMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleChangeUserAccess = useCallback(async (): Promise<void> => {
    if (userId && updatedData) {
      try {
        await changeUserAccess({
          userId,
          params: updatedData,
        }).unwrap();
        showNotification('info', t('Success'), t('User has been successfully updated'));
        onSuccess?.();
      } catch (error) {
        console.log(error);
        showNotification('error', t('Error'), t('Error when updating user'));
      } finally {
        closeConfirmModal();
      }
    }
  }, [userId, updatedData, changeUserAccess, t, onSuccess, closeConfirmModal]);

  return {
    openConfirmModal,
    closeConfirmModal,
    handleChangeUserAccess,
    isOpenedConfirm,
    isLoading,
  };
};
